import {
  API_ORIGIN,
  getListPagination,
  appendListPageParams,
  authorizedFetch,
} from '@yeshuman/sdk'
import type { PaginatedListMeta, CandidateMatchBand } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'

export type ApplicationFitNotes = {
  evaluation_id: string
  highlights: string[]
  red_flags: string[]
  recommendation: string
  source: string
  analysed_at: string
}

export type ApplicationAnswerRow = {
  id: string
  question_id: string
  answer_text: string
  answer_options: string[]
  is_disqualifying: boolean
}

export interface MyApplication {
  id: string
  profile_id: string
  opportunity_id?: string
  opportunity_title: string
  opportunity_slug?: string | null
  organisation_name: string
  status:
    | 'draft'
    | 'applied'
    | 'invited'
    | 'in_review'
    | 'interview'
    | 'offer'
    | 'hired'
    | 'rejected'
    | 'withdrawn'
  source: string
  applied_at: string
  cover_letter?: string
  answers?: ApplicationAnswerRow[]
  match_band?: CandidateMatchBand
  fit_notes?: ApplicationFitNotes | null
  current_stage?: {
    id: string
    stage_name: string
    entered_at: string
    is_open: boolean
  }
}

export type ApplicationStatusEntry = {
  id: string
  status: string
  applied_at: string
}

export type ApplicationStatusMap = Record<string, ApplicationStatusEntry>

/** Index applications by opportunity for card trailing states and saved-job filtering. */
export function buildApplicationStatusMap(applications: MyApplication[]): ApplicationStatusMap {
  const map: ApplicationStatusMap = {}
  for (const app of applications) {
    if (app.opportunity_id) {
      map[app.opportunity_id] = {
        id: app.id,
        status: app.status,
        applied_at: app.applied_at,
      }
    }
  }
  return map
}

export type PaginatedMyApplications = {
  results: MyApplication[]
  page: number
  page_size: number
  total: number
  has_next: boolean
}

/**
 * Candidate's own applications. The 'my-applications' key is invalidated by the
 * SSE content_update handler when the agent's manage_applications tool runs,
 * so subscribers live-refresh during chat.
 *
 * Omit `page` to fetch the full list (status maps on job cards). Pass `page` for the Applied tab list.
 */
export function useMyApplications(options?: {
  enabled?: boolean
  page?: number
  pageSize?: number
}) {
  const listPagination = getListPagination('candidateApplied')
  const paginateList = listPagination.enabled && options?.page != null
  const page = options?.page ?? 1
  const pageSize = options?.pageSize ?? listPagination.pageSize

  const query = useQuery({
    queryKey: paginateList
      ? (['my-applications', 'page', page, pageSize] as const)
      : (['my-applications'] as const),
    queryFn: async (): Promise<PaginatedMyApplications> => {
      const params = new URLSearchParams()
      if (paginateList) {
        appendListPageParams(params, page, pageSize)
      }
      const qs = params.toString()
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/my${qs ? `?${qs}` : ''}`,
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch applications (${response.status})`)
      }
      return (await response.json()) as PaginatedMyApplications
    },
    enabled: options?.enabled ?? true,
  })

  const pagination: PaginatedListMeta | undefined = query.data
    ? {
        page: query.data.page,
        page_size: query.data.page_size,
        total: query.data.total,
        has_next: query.data.has_next,
      }
    : undefined

  return {
    applications: query.data?.results ?? [],
    pagination,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : query.error ? 'Failed to load applications' : null,
    refetch: query.refetch,
  }
}

/** One application the candidate owns — invalidated by manage_applications content_update. */
export function useMyApplication(applicationId: string | undefined) {
  return useQuery({
    queryKey: ['application', 'my', applicationId],
    enabled: Boolean(applicationId),
    queryFn: async (): Promise<MyApplication> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/my/${applicationId}`,
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch application (${response.status})`)
      }
      return (await response.json()) as MyApplication
    },
  })
}

/** Candidate-safe event row — no actor_id, metadata already sanitized per type by the API. */
export type CandidateApplicationEvent = {
  id: string
  event_type: 'applied' | 'invited' | 'stage_changed' | 'decision_made' | 'withdrawn' | 'note_added'
  metadata: Record<string, unknown>
  created_at: string
}

/** Status timeline for the Applied artifact canvas; ascending by created_at. */
export function useApplicationEvents(applicationId: string | undefined) {
  const query = useQuery({
    queryKey: ['application-events', 'my', applicationId],
    enabled: Boolean(applicationId),
    queryFn: async (): Promise<CandidateApplicationEvent[]> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/my/${applicationId}/events/`,
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch application history (${response.status})`)
      }
      const data = await response.json()
      return Array.isArray(data) ? data : []
    },
  })

  return {
    events: query.data ?? [],
    isLoading: query.isLoading,
    error:
      query.error instanceof Error
        ? query.error.message
        : query.error
          ? 'Failed to load application history'
          : null,
  }
}
